(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initRoot = (root) => {
    if (!root || root.dataset.satanReady === 'true') return;
    root.dataset.satanReady = 'true';
    const rootSelector = '[data-satan-landing], [data-satan-product], [data-satan-product-reviews]';
    const ownedItems = (selector, scope = root) => [...scope.querySelectorAll(selector)]
      .filter((item) => item.closest(rootSelector) === root);
    let runAfterScroll = (callback) => callback();
    if (root.matches('[data-satan-landing]')) {
      runAfterScroll = (callback) => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 3200 });
        else window.setTimeout(callback, 600);
      };
      const lazyFeatures = [
        {
          target: root.querySelector('[data-ad-diagnosis]'),
          script: root.dataset.diagnosisScript,
          readyKey: 'diagnosisReady',
        },
        {
          target: root.querySelector('[data-ad-decision-simulator]'),
          script: root.dataset.decisionScript,
          readyKey: 'decisionReady',
        },
      ].filter((feature) => feature.target);
      const loadFeature = (feature) => {
        if (feature.target.dataset[feature.readyKey] === 'true') return Promise.resolve();
        if (feature.promise) return feature.promise;
        feature.promise = new Promise((resolve) => {
          const existing = document.querySelector(`script[src="${feature.script}"]`);
          const script = existing || document.createElement('script');
          const complete = () => {
            feature.ready = true;
            resolve();
          };
          script.addEventListener('load', complete, { once: true });
          if (!existing) {
            script.src = feature.script;
            script.async = true;
            document.head.append(script);
          } else if (feature.target.dataset[feature.readyKey] === 'true') {
            complete();
          }
        });
        return feature.promise;
      };
      const queueFeature = (feature) => {
        runAfterScroll(() => loadFeature(feature));
      };
      if ('IntersectionObserver' in window) {
        const featureObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const feature = lazyFeatures.find((item) => item.target === entry.target);
            if (feature) queueFeature(feature);
            featureObserver.unobserve(entry.target);
          });
        }, { rootMargin: '500px 0px' });
        lazyFeatures.forEach((feature) => featureObserver.observe(feature.target));
      } else {
        lazyFeatures.forEach(loadFeature);
      }
      root.addEventListener('click', (event) => {
        const feature = lazyFeatures.find((item) => item.target.contains(event.target));
        const trigger = event.target.closest('button, a');
        if (!feature || !trigger || feature.target.dataset[feature.readyKey] === 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        loadFeature(feature).then(() => trigger.click());
      }, true);
    }
    const videoFacade = root.querySelector('[data-video-facade]');
    if (videoFacade) {
      const playButton = videoFacade.querySelector('.satan-video-facade__button');
      const poster = videoFacade.querySelector('[data-video-poster-src]');
      let posterObserver;
      const loadPoster = () => {
        if (!poster?.isConnected || !poster.dataset.videoPosterSrc) return;
        poster.sizes = poster.dataset.videoPosterSizes || '100vw';
        poster.srcset = poster.dataset.videoPosterSrcset || '';
        poster.src = poster.dataset.videoPosterSrc;
        delete poster.dataset.videoPosterSrc;
        delete poster.dataset.videoPosterSrcset;
        delete poster.dataset.videoPosterSizes;
        poster.addEventListener('load', () => {
          const decode = () => poster.decode?.().catch(() => {});
          runAfterScroll(() => {
            if ('requestIdleCallback' in window) window.requestIdleCallback(decode, { timeout: 1800 });
            else window.setTimeout(decode, 80);
          });
        }, { once: true });
      };
      if (poster && 'IntersectionObserver' in window) {
        const posterScrollRoot = null;
        posterObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          posterObserver.disconnect();
          runAfterScroll(loadPoster);
        }, {
          root: posterScrollRoot?.contains(root) ? posterScrollRoot : null,
          rootMargin: '100px 0px',
        });
        posterObserver.observe(videoFacade);
      } else {
        loadPoster();
      }
      playButton?.addEventListener('click', () => {
        const videoSrc = videoFacade.dataset.videoSrc;
        if (!videoSrc || videoFacade.classList.contains('is-playing')) return;

        posterObserver?.disconnect();
        const iframe = document.createElement('iframe');
        iframe.className = 'satan-video-embed';
        iframe.src = videoSrc + '&autoplay=1';
        iframe.title = 'SatanReklamlar eğitim tanıtım videosu';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        videoFacade.classList.add('is-playing');
        videoFacade.replaceChildren(iframe);
      }, { once: true });
    }

    // Render content in its final state immediately. Scroll-triggered reveal and
    // count-up observers caused repeated style recalculation on this long page.
    if (!root.matches('[data-satan-landing], [data-satan-product]')) {
      ownedItems('[data-reveal]').forEach((item) => item.classList.add('is-visible'));
    }
    ownedItems('[data-count-up]').forEach((counter) => {
      const target = Number.parseInt(counter.dataset.countUp, 10);
      if (Number.isFinite(target)) counter.textContent = target.toLocaleString('tr-TR');
    });

    ownedItems('.satan-review-stage').forEach((stage) => {
      const rail = stage.querySelector('.satan-reviews--rail');
      const previous = stage.querySelector('[data-review-prev]');
      const next = stage.querySelector('[data-review-next]');
      if (!rail) return;

      const move = (direction) => {
        const card = rail.querySelector('.satan-review-card');
        const distance = card ? card.getBoundingClientRect().width + 18 : 360;
        rail.scrollBy({ left: distance * direction, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      };
      previous?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
    });

    ownedItems('[data-review-gallery]').forEach((gallery) => {
      const toggle = gallery.nextElementSibling;
      if (!toggle?.matches('[data-review-toggle]')) return;

      const template = gallery.querySelector('[data-review-extra-template]');
      const label = toggle.querySelector('[data-review-toggle-label]');
      const count = toggle.querySelector('[data-review-toggle-count]');
      const hiddenCount = Number.parseInt(toggle.dataset.reviewHiddenCount || '0', 10);
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        const nextExpanded = !expanded;
        gallery.classList.toggle('is-expanded', nextExpanded);
        toggle.setAttribute('aria-expanded', String(nextExpanded));
        if (nextExpanded && template && !gallery.querySelector('[data-review-extra]')) {
          gallery.append(template.content.cloneNode(true));
        } else if (!nextExpanded) {
          gallery.querySelectorAll('[data-review-extra]').forEach((item) => item.remove());
        }
        if (label) label.textContent = expanded ? 'Devamını gör' : 'Daha az göster';
        if (count) count.textContent = expanded ? `${hiddenCount} yorum daha` : 'İlk 10 yoruma dön';
        if (expanded && gallery.getBoundingClientRect().top < 0) {
          gallery.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        }
      });
    });

    const curriculumToggle = root.querySelector('[data-curriculum-toggle]');
    if (curriculumToggle) {
      const curriculumList = root.querySelector('[data-curriculum-list]');
      const curriculumTemplate = root.querySelector('[data-curriculum-extra-template]');
      const toggleLabel = curriculumToggle.querySelector('[data-curriculum-toggle-label]');
      const toggleCount = curriculumToggle.querySelector('small');
      const hiddenCount = curriculumTemplate?.content.querySelectorAll('[data-curriculum-extra]').length || 0;

      curriculumToggle.addEventListener('click', () => {
        const expanded = curriculumToggle.getAttribute('aria-expanded') === 'true';
        const nextExpanded = !expanded;
        curriculumToggle.setAttribute('aria-expanded', String(nextExpanded));
        if (nextExpanded && curriculumTemplate && !curriculumList?.querySelector('[data-curriculum-extra]')) {
          const fragment = curriculumTemplate.content.cloneNode(true);
          fragment.querySelectorAll('[data-curriculum-extra]').forEach((item) => item.classList.add('is-visible'));
          curriculumList?.append(fragment);
        } else if (!nextExpanded) {
          curriculumList?.querySelectorAll('[data-curriculum-extra]').forEach((item) => item.remove());
        }
        if (toggleLabel) toggleLabel.textContent = nextExpanded ? 'Daha az göster' : 'Devamını gör; daha bitmedi!';
        if (toggleCount) toggleCount.textContent = nextExpanded ? 'İlk 20 başlığa dön' : `${hiddenCount} başlık daha`;
        if (!nextExpanded && curriculumList?.getBoundingClientRect().top < 0) {
          curriculumList.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        }
      });
    }
    const dialog = root.querySelector('[data-review-dialog]');
    const dialogImage = dialog?.querySelector('[data-review-dialog-image]');
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-review-open]');
      if (!button || button.closest(rootSelector) !== root) return;
      const source = button.querySelector('img');
      if (!dialog || !dialogImage || !source) return;
      dialogImage.src = source.dataset.reviewFullSrc || source.currentSrc || source.src;
      dialogImage.width = Number(source.width) || source.naturalWidth;
      dialogImage.height = Number(source.height) || source.naturalHeight;
      dialogImage.alt = source.alt;
      if (typeof dialog.showModal === 'function') dialog.showModal();
    });

    dialog?.querySelector('[data-review-close]')?.addEventListener('click', () => dialog.close());
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    const timedOffer = root.querySelector('[data-timed-offer]');
    if (timedOffer) {
      const params = new URLSearchParams(window.location.search);
      const previewOffer = params.get('preview_promo') === '1';
      const enabled = timedOffer.dataset.enabled === 'true';
      const variantId = Number.parseInt(timedOffer.dataset.variantId || '', 10);
      const discountCode = timedOffer.dataset.discountCode || '';
      const delayMs = Number.parseInt(timedOffer.dataset.delayMs || '300000', 10);
      const durationMs = Number.parseInt(timedOffer.dataset.durationMs || '60000', 10);
      const countdown = timedOffer.querySelector('[data-timed-offer-countdown]');
      const cartButton = timedOffer.querySelector('[data-timed-offer-cart]');
      const status = timedOffer.querySelector('[data-timed-offer-status]');
      const storageKey = `sr_timed_offer_${discountCode || 'offer'}`;
      let timerId;

      const readSession = (key) => {
        if (previewOffer) return null;
        try { return window.sessionStorage.getItem(`${storageKey}_${key}`); } catch (error) { return null; }
      };
      const writeSession = (key, value) => {
        if (previewOffer) return;
        try { window.sessionStorage.setItem(`${storageKey}_${key}`, String(value)); } catch (error) { /* no-op */ }
      };
      const formatTime = (remaining) => {
        const seconds = Math.max(0, Math.ceil(remaining / 1000));
        return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
      };
      const closeOffer = (reason = 'dismissed') => {
        window.clearInterval(timerId);
        timedOffer.classList.remove('is-open');
        timedOffer.setAttribute('aria-hidden', 'true');
        writeSession(reason, 'true');
        window.setTimeout(() => { timedOffer.hidden = true; }, reduceMotion.matches ? 0 : 240);
      };
      const runCountdown = (deadline) => {
        const update = () => {
          const remaining = deadline - Date.now();
          if (countdown) countdown.textContent = formatTime(remaining);
          if (remaining > 0) return;
          if (status) status.textContent = 'Bu teklifin süresi doldu.';
          closeOffer('expired');
        };
        update();
        timerId = window.setInterval(update, 250);
      };
      const openOffer = () => {
        if (readSession('dismissed') || readSession('expired')) return;
        timedOffer.hidden = false;
        timedOffer.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => timedOffer.classList.add('is-open'));
        const storedDeadline = Number.parseInt(readSession('deadline') || '', 10);
        const deadline = Number.isFinite(storedDeadline) && storedDeadline > Date.now()
          ? storedDeadline
          : Date.now() + durationMs;
        writeSession('deadline', deadline);
        runCountdown(deadline);
        timedOffer.querySelector('[data-timed-offer-close]')?.focus({ preventScroll: true });
      };

      timedOffer.querySelectorAll('[data-timed-offer-close]').forEach((button) => {
        button.addEventListener('click', () => closeOffer('dismissed'));
      });
      cartButton?.addEventListener('click', async () => {
        if (!Number.isFinite(variantId) || !discountCode) {
          if (status) status.textContent = 'Teklif ayarları tamamlanmadı.';
          return;
        }
        cartButton.disabled = true;
        cartButton.classList.add('is-loading');
        if (status) status.textContent = 'Sepetin hazırlanıyor…';
        try {
          const routesRoot = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${routesRoot}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
          if (!response.ok) throw new Error('cart-add-failed');
          writeSession('claimed', 'true');
          const redirect = encodeURIComponent(`${routesRoot}cart`);
          window.location.assign(`${routesRoot}discount/${encodeURIComponent(discountCode)}?redirect=${redirect}`);
        } catch (error) {
          cartButton.disabled = false;
          cartButton.classList.remove('is-loading');
          if (status) status.textContent = 'Teklif şu anda sepete eklenemiyor. Lütfen ürün ayarlarını kontrol et.';
        }
      });

      if (enabled || previewOffer) {
        const startedAt = Number.parseInt(readSession('started_at') || '', 10) || Date.now();
        writeSession('started_at', startedAt);
        const waitMs = previewOffer ? 350 : Math.max(0, delayMs - (Date.now() - startedAt));
        window.setTimeout(openOffer, waitMs);
      }
    }
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-satan-landing], [data-satan-product], [data-satan-product-reviews]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
